import {NullableDate, NullableString} from "../../../../types/nullable-types";
import {IDotRezContact} from "../../../dot-rez-api/data-contracts/booking/booking-state/booking-state.data-contracts";
import {IBookingContactViewModel} from "./booking-contact-view-model.interface";
import {IBookingContactFields} from "./booking-contact-fields.interface";
import {FormModel} from "../../../../models/forms/form.model";
import {BookingModel} from "../booking.model";
import {MaturePassengerModel} from "../passenger/mature-passenger.model";
import {IReactionDisposer, makeObservable, observable, reaction, runInAction} from "mobx";
import {IPersistedBookingContact} from "./persisted-booking-contact.interface";
import {EMailValidator} from "../../../../models/forms/field-validators/e-mail.validator";
import {PersonNameValidator} from "../../../../models/forms/field-validators/person-name.validator";
import {FormFields} from "../../../../models/forms/form-field.interface";
import {PhoneTypeEnum} from "../../../dot-rez-api/data-contracts/enums/phone-type.enum";
import {DistributionOptionsEnum} from "../../../dot-rez-api/data-contracts/enums/distribution-options.enum";
import {NotificationPreferenceEnum} from "../../../dot-rez-api/data-contracts/enums/notification-preference.enum";
import {BookingSessionStorageKeys} from "../storage/booking-session-storage-keys";
import {getPhoneNumberCountry} from "../../../../components/forms/contact/phone-number-helpers";
import {UserLocalStorageKeys} from "../../../storage/user-local-storage/user-local-storage-keys";


export const PRIMARY_CONTACT_KEY = 'P';

export class BookingContactModel extends FormModel<IBookingContactFields> implements IBookingContactViewModel {
    constructor(private readonly booking: BookingModel) {
        super(booking.services);
        this._dataPrivacyAccepted = this.booking.storage.getItem(BookingSessionStorageKeys.dataPrivacyAccepted) === 'true';

        if(this.booking.shouldSyncContactWithPrimaryPassenger) {
            this._reactions.push(reaction(() => [...booking.passengers.map(p => p.isPrimaryContact)],
                () => {
                    this._subscribeToPrimaryPassengerChanges();
                    this._syncNameWithPrimaryPassenger();
                },
                {
                    fireImmediately: true
                }));
        }

        makeObservable<this, '_dataPrivacyAccepted'>(this, {
            _dataPrivacyAccepted: observable.ref
        });
    }

    private _reactions: IReactionDisposer[] = [];

    dispose(): void {
        this._reactions.forEach(r => r());
        this._reactions = [];
    }



    private get initialContactSnapshot(): IDotRezContact | null | undefined {
        if(!this.booking.initialBookingSnapshot) {
            return null;
        }
        return this.booking.initialBookingSnapshot.bookingData.contacts.find(c => c.key === PRIMARY_CONTACT_KEY)?.value;
    }

    protected _createFields(): FormFields<IBookingContactFields> {
        const persistedContact = this.getPersistedBookingContact();
        const language = this.services.language;

        return {
            firstName: this._createField<NullableString>({
                fieldName: () => language.translate('First name'),
                getValueFromPreviousSession: () => this.initialContactSnapshot?.name?.first,
                initialValue: () => this.data.name?.first,
                defaultValue: persistedContact?.firstName,
                autoCapitalize: true,
                maxLength: 32,
                validators: [
                    new PersonNameValidator(language.translate('Not a valid first name'))
                ]
            }),
            lastName: this._createField<NullableString>({
                fieldName: () => language.translate('Last name'),
                getValueFromPreviousSession: () => this.initialContactSnapshot?.name?.last,
                initialValue: () => this.data.name?.last,
                defaultValue: persistedContact?.lastName,
                autoCapitalize: true,
                maxLength: 32,
                validators: [
                    new PersonNameValidator(language.translate('Not a valid last name'))
                ]
            }),
            emailAddress: this._createField<NullableString>({
                fieldName: () => language.translate('E-mail'),
                getValueFromPreviousSession: () => this.initialContactSnapshot?.emailAddress,
                initialValue: () => this.data.emailAddress,
                defaultValue: persistedContact?.emailAddress,
                maxLength: 250,
                validators: [
                    new EMailValidator(this.services)
                ]
            }),
            countryCode: this._createField<NullableString>({
                fieldName: () => language.translate('Country of residence'),
                getValueFromPreviousSession: () => this.initialContactSnapshot?.address?.countryCode,
                initialValue: () => this.data.address?.countryCode,
                defaultValue: persistedContact?.countryCode,
                maxLength: 2,
                isRequired: false
            }),
            phoneNumber: this._createPhoneField({
                getValueFromPreviousSession: () => this.initialContactSnapshot?.phoneNumbers && this.initialContactSnapshot.phoneNumbers[0]?.number,
                initialValue: () =>  this.data.phoneNumbers && this.data.phoneNumbers[0]?.number,
                defaultValue: persistedContact?.phoneNumber
            }),
            customerNumber: this._createField<string>({
                fieldName: () => language.translate('Customer number'),
                //getValueFromPreviousSession: () => this.services.user.profile.customerNumber,
                isRequired: false,
                isHidden: () => true,
                defaultValue: this.services.user.profile.customerNumber
            }),
            dateOfBirth: this._createField<NullableDate>({
                fieldName: () => language.translate('Date of birth'),
                //getValueFromPreviousSession: () => this.services.time.tryConvertToDate(persistedContact?.dateOfBirth),
                defaultValue: this.services.time.tryConvertToDate(persistedContact?.dateOfBirth),
                isRequired: false,
                isHidden: () => true
            })
        };
    }


    get fullContactName(): NullableString {
        if(this.fields.firstName.value && this.fields.lastName.value) {
            return `${this.fields.firstName.value} ${this.fields.lastName.value}`;
        }

        return null;
    }

    private _primaryPassengerChangesSubscription: IReactionDisposer | null = null;
    private _subscribeToPrimaryPassengerChanges(): void {
        const primaryContact = this.getPrimaryContactPassenger();
        if(!primaryContact) {
            return;
        }

        if(this._primaryPassengerChangesSubscription) {
            this._primaryPassengerChangesSubscription();
        }

        this._primaryPassengerChangesSubscription = reaction(() => [
            primaryContact.fields.firstName.value,
            primaryContact.fields.lastName.value
        ], () => {
            this._syncNameWithPrimaryPassenger();
        });
    }

    private _syncNameWithPrimaryPassenger(): void {
        const primaryContact = this.getPrimaryContactPassenger();
        if(primaryContact) {
            this.fields.firstName.setValue(primaryContact.fields.firstName.value?.toUpperCase()?.trim() || null);
            this.fields.lastName.setValue(primaryContact.fields.lastName.value?.toUpperCase().trim() || null);
        }
    }

    private _getPrimaryContact(): IDotRezContact | null {
        return this.booking.bookingData.contacts.filter(item => item.key === PRIMARY_CONTACT_KEY)[0]?.value;
    }

    private get _isNew(): boolean {
        return !Boolean(this._getPrimaryContact()?.name?.first);
    }

    private get data(): IDotRezContact {

        let dotRezContact = this._getPrimaryContact();
        if(!dotRezContact) {
            dotRezContact = {
                contactTypeCode: PRIMARY_CONTACT_KEY,
                customerNumber: this.services.user.profile.customerNumber,
                cultureCode: this.services.language.currentLanguage,
                distributionOption: DistributionOptionsEnum.Email,
                companyName: null,
                notificationPreference: NotificationPreferenceEnum.None,
                sourceOrganization: null,
                address: {
                    city: null,
                    countryCode: null,
                    lineOne: null,
                    postalCode: null,
                    provinceState: null
                },
                name: {
                    first: null,
                    last: null,
                    title: null,
                },
                emailAddress: null,
                phoneNumbers: [],
            }
            runInAction(() => {
                this.booking.bookingData.contacts.push({
                    key: PRIMARY_CONTACT_KEY,
                    value: dotRezContact!
                });
            });

        }
        return dotRezContact;
    }

    private _getUpdatedContactContactData(): IDotRezContact {
        return {
            ...this.data,
            name: {
                ...this.data.name,
                first: this.fields.firstName.value?.toUpperCase()?.trim() || null,
                last: this.fields.lastName.value?.toUpperCase()?.trim() || null
            },
            customerNumber: this.fields.customerNumber.value || '',
            emailAddress: this.fields.emailAddress.value,
            cultureCode: this.services.language.currentLanguage,
            distributionOption: DistributionOptionsEnum.Email,
            address: {
                ...this.data.address,
                countryCode: this.fields.countryCode.value,
            },
            phoneNumbers: [
                {
                    number: this.fields.phoneNumber.value,
                    type: PhoneTypeEnum.Home
                }
            ]
        };
    }

    async save(): Promise<void> {
        if(!this.hasChanges()) {
            return;
        }

        let updatedContactData: IDotRezContact = this._getUpdatedContactContactData();

        if(this._isNew) {
            await this.booking.addContact(updatedContactData);
        } else {
            await this.booking.updateContact(updatedContactData);
        }

        runInAction(() => {
            const index = this.booking.bookingData.contacts.findIndex(c => c.key === PRIMARY_CONTACT_KEY);
            this.booking.bookingData.contacts[index].value = updatedContactData;
        });

        this.commitChanges();

        this._persistBookingContact();
    }

    private get _allowBookingContactPersist(): boolean {
        return this.services.configuration.allowPersistContactInfo
            && this.booking.isNewBooking;
    }

    getPersistedBookingContact(): IPersistedBookingContact | null | undefined  {
        if(!this._allowBookingContactPersist) {
            return null;
        }

        return this.booking.services.userLocalStorage.getJson<IPersistedBookingContact>(UserLocalStorageKeys.UserProfileBookingContact);
    }

    private _persistBookingContact(): void {
        if(!this._allowBookingContactPersist) {
            return;
        }
        const persistedContact: IPersistedBookingContact = {
            firstName: this.fields.firstName.value?.toUpperCase()?.trim() || null,
            lastName: this.fields.lastName.value?.toUpperCase()?.trim() || null,
            emailAddress: this.fields.emailAddress.value,
            phoneNumber: this.fields.phoneNumber.value,
            countryCode: this.fields.countryCode.value,
            dateOfBirth: this.services.time.formatYYYY_MM_DD(this.fields.dateOfBirth.value)
        };

        this.booking.services.userLocalStorage.setJson(UserLocalStorageKeys.UserProfileBookingContact, persistedContact);
    }


    private getPrimaryContactPassenger(): MaturePassengerModel | null {
        return this.booking.passengers.find(p => p.isPrimaryContact) ?? null;
    }

    private _dataPrivacyAccepted: boolean = false;
    get dataPrivacyAccepted(): boolean {
        return this._dataPrivacyAccepted;
    }
    set dataPrivacyAccepted(value: boolean) {
        runInAction(() => {
            this._dataPrivacyAccepted = value;
            if(value) {
                this.booking.storage.setItem(BookingSessionStorageKeys.dataPrivacyAccepted, 'true');
            } else {
                this.booking.storage.removeItem(BookingSessionStorageKeys.dataPrivacyAccepted);
            }

        })
    }

    getCountryCode(): NullableString {
        if(this.fields.countryCode.value) {
            return this.fields.countryCode.value;
        }

        let phoneNumberValue = this.fields.phoneNumber.value;
        if(!phoneNumberValue) {
            return null;
        }

        return getPhoneNumberCountry(phoneNumberValue);
    }

    copyFromAgentProfile(): void {
        const primaryPassenger = this.booking.passengers.find(p => p.isPrimaryContact);
        if(primaryPassenger) {
            primaryPassenger.isPrimaryContact = false;
        }

        const userProfile = this.services.user.profile;
        this.fields.firstName.setValue(userProfile.firstName);
        this.fields.lastName.setValue(userProfile.lastName);
        this.fields.emailAddress.setValue(userProfile.emailAddress);
        this.fields.phoneNumber.setValue(userProfile.workPhoneNumber || userProfile.phoneNumber);
        this.fields.countryCode.setValue(getPhoneNumberCountry(this.fields.phoneNumber.value));
        this.fields.dateOfBirth.setValue(null);
        this.fields.customerNumber.setValue(userProfile.customerNumber);
    }
}
